import React from 'react';

interface CTA {
  text: string;
  href?: string;
  onClick?: () => void;
}

interface Card {
  headline: string;
  description: string;
  primaryCta?: CTA;
  secondaryCta?: CTA;
}

interface MainImage {
  url: string;
  alt: string;
  title?: string;
  subtitle?: string;
}

interface ImageCardGridProps {
  mainImage: MainImage;
  cards?: Card[];
  imagePosition?: 'left' | 'right';
}

function ImageCardGrid({
  mainImage,
  cards = [],
  imagePosition = 'left',
}: ImageCardGridProps) {
  const maxCards = cards.slice(0, 4);

  const imageSection = (
    <div className="au-relative au-bg-[#161616] au-rounded-lg au-overflow-hidden au-flex au-items-end au-h-[500px]">
      <div
        className="au-absolute au-inset-0 au-bg-cover au-bg-center au-bg-no-repeat"
        style={{ backgroundImage: `url(${mainImage.url})` }}
      >
        <div className="au-absolute au-inset-0 au-bg-black au-bg-opacity-40" />
      </div>

      <div className="au-relative au-z-10 au-p-6 au-text-white">
        {mainImage.title && (
          <h2 className="au-text-white au-text-3xl au-font-bold au-mb-2">
            {mainImage.title}
          </h2>
        )}
        {mainImage.subtitle && (
          <p className="au-text-white au-text-lg au-opacity-90">
            {mainImage.subtitle}
          </p>
        )}
      </div>
    </div>
  );

  const cardsSection = (
    <div className="au-grid au-grid-cols-1 md:au-grid-cols-2 au-gap-2 au-h-[500px]">
      {maxCards.map(card => (
        <div
          key={card.headline}
          className="au-bg-[#161616] au-border au-border-white au-rounded-lg au-p-6 au-flex au-flex-col au-h-full"
        >
          <h3 className="au-text-[#FEFF09] au-text-lg au-font-bold au-mb-3">
            {card.headline}
          </h3>
          <p className="au-text-white au-text-sm au-leading-relaxed au-mb-4 au-flex-grow">
            {card.description}
          </p>

          {/* CTAs */}
          <div className="au-flex au-gap-3 au-flex-wrap au-mt-auto">
            {card.primaryCta && (
              <button
                className="au-border au-border-white au-text-white au-px-4 au-py-2 au-rounded au-text-sm au-hover:au-bg-white au-hover:au-text-[#161616] au-transition-colors"
                onClick={card.primaryCta.onClick}
                type="button"
              >
                {card.primaryCta.text}
              </button>
            )}
            {card.secondaryCta && (
              <button
                className="au-border au-border-white au-text-white au-px-4 au-py-2 au-rounded au-text-sm au-hover:au-bg-white au-hover:au-text-[#161616] au-transition-colors"
                onClick={card.secondaryCta.onClick}
                type="button"
              >
                {card.secondaryCta.text}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="au-p-6">
      <div className="au-flex au-flex-col lg:au-flex-row au-gap-4 au-items-start">
        {imagePosition === 'left' ? (
          <>
            <div className="au-w-full lg:au-w-[30%]">{imageSection}</div>
            <div className="au-w-full lg:au-w-[70%]">{cardsSection}</div>
          </>
        ) : (
          <>
            <div className="au-w-full lg:au-w-[70%]">{cardsSection}</div>
            <div className="au-w-full lg:au-w-[30%]">{imageSection}</div>
          </>
        )}
      </div>
    </div>
  );
}

ImageCardGrid.attributes = {
  mainImage: {
    type: 'object',
    default: { url: '', alt: '' },
  },
  cards: {
    type: 'array',
    default: [],
  },
  imagePosition: {
    type: 'string',
    default: 'left',
  },
};

export default ImageCardGrid;
